export const validEmailRegex =
  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/

export const isValidEmailAddress = (email: string) =>
  validEmailRegex.test(email)

/**
 * Validates a UK phone number
 * @param phoneNumber - The phone number to validate
 * @returns Validation result with isValid flag and error message
 */
export const validateUKPhoneNumber = (
  phoneNumber: string,
): { isValid: boolean; error?: string } => {
  if (!phoneNumber.trim()) {
    return { isValid: false, error: 'Phone number is required' }
  }

  const normalizedNumber = phoneNumber.trim().replace(/\s+/g, ' ')

  // Remove all non-digits for length checking
  const digitsOnly = normalizedNumber.replace(/\D/g, '')

  // Check if it starts with +44 or 0
  if (!/^(\+44\s?|0)/.test(normalizedNumber)) {
    return {
      isValid: false,
      error: 'Please enter a valid phone number',
    }
  }

  // Check total length
  if (normalizedNumber.startsWith('+44')) {
    // International format: should have 10 digits after +44
    if (digitsOnly.length !== 12) {
      // +44 (2 digits) + 10 digits = 12 total
      return {
        isValid: false,
        error: 'Please enter a valid phone number',
      }
    }
  } else {
    // Domestic format: should have 11 digits
    if (digitsOnly.length !== 11) {
      return {
        isValid: false,
        error: 'Please enter a valid phone number',
      }
    }
  }

  return { isValid: true }
}

/**
 * Validates a Canadian phone number
 * @param phoneNumber - The phone number to validate
 * @returns Validation result with isValid flag and error message
 */
export const validateCAPhoneNumber = (
  phoneNumber: string,
): { isValid: boolean; error?: string } => {
  if (!phoneNumber.trim()) {
    return { isValid: false, error: 'Phone number is required' }
  }

  const normalizedNumber = phoneNumber.trim().replace(/\s+/g, ' ')

  // Remove all non-digits for length checking
  const digitsOnly = normalizedNumber.replace(/\D/g, '')

  // Check if it starts with +1 or area code (more flexible)
  if (!/^(\+1\s*|\(?[1-9]\d{2}\)?)/.test(normalizedNumber)) {
    return {
      isValid: false,
      error: 'Please enter a valid phone number',
    }
  }

  // Check total length
  if (normalizedNumber.startsWith('+1')) {
    // International format: should have 10 digits after +1
    if (digitsOnly.length !== 11) {
      return {
        isValid: false,
        error: 'Please enter a valid phone number',
      }
    }
  } else {
    // Domestic format: should have 10 digits
    if (digitsOnly.length !== 10) {
      return {
        isValid: false,
        error: 'Please enter a valid phone number',
      }
    }
  }

  return { isValid: true }
}

// Postal code validation patterns by country
export const postalCodePatterns: Record<string, RegExp> = {
  // Netherlands (4 digits, 2 letters)
  NL: /^[1-9][0-9]{3}\s?[A-Z]{2}$/i,

  // United States (5 digits or 5+4 format)
  US: /^\d{5}(-\d{4})?$/,

  // United Kingdom (various formats)
  GB: /^[A-Z]{1,2}\d[A-Z\d]?\s?\d[A-Z]{2}$/i,

  // Germany (5 digits)
  DE: /^\d{5}$/,

  // France (5 digits)
  FR: /^\d{5}$/,

  // Belgium (4 digits)
  BE: /^\d{4}$/,

  // Italy (5 digits)
  IT: /^\d{5}$/,

  // Sweden (5 digits)
  SE: /^\d{5}$/,

  // Switzerland (4 digits)
  CH: /^\d{4}$/,

  // Poland (5 digits)
  PL: /^\d{5}$/,

  // Spain (5 digits)
  ES: /^\d{5}$/,

  // Finland (5 digits)
  FI: /^\d{5}$/,

  // Denmark (4 digits)
  DK: /^\d{4}$/,

  // Austria (4 digits)
  AT: /^\d{4}$/,

  // Norway (4 digits)
  NO: /^\d{4}$/,

  // Czech Republic (5 digits)
  CZ: /^\d{5}$/,

  // Croatia (5 digits)
  HR: /^\d{5}$/,

  // Hungary (4 digits)
  HU: /^\d{4}$/,

  // Estonia (5 digits)
  EE: /^\d{5}$/,

  // Latvia (4 digits)
  LV: /^\d{4}$/,

  // Lithuania (5 digits)
  LT: /^\d{5}$/,

  // Greece (5 digits)
  GR: /^\d{5}$/,

  // Canada (A1A 1A1 format)
  CA: /^[A-Z]\d[A-Z]\s?\d[A-Z]\d$/i,
}

// Generic postal code validation (allows alphanumeric with spaces)
export const genericPostalCodePattern = /^[A-Z0-9\s-]{3,10}$/i

/**
 * Cleans a postal code input by removing country codes and extra text
 * @param input - The raw postal code input
 * @returns Cleaned postal code
 */
export const cleanPostalCodeInput = (input: string): string => {
  if (!input) return ''

  // Remove common country codes at the end (GB, UK, US, etc.)
  const cleaned = input
    .trim()
    .replace(
      /\s+(GB|UK|US|CA|NL|DE|FR|IT|ES|SE|CH|PL|BE|FI|DK|AT|NO|CZ|HR|HU|EE|LV|LT|GR)$/i,
      '',
    )
    .trim()

  return cleaned
}

/**
 * Validates a postal code for a specific country
 * @param postalCode - The postal code to validate
 * @param countryCode - The country code (e.g., 'NL', 'US', 'GB')
 * @returns Validation result with isValid flag and error message
 */
export const validatePostalCode = (
  postalCode: string,
  countryCode: string,
): { isValid: boolean; error?: string } => {
  if (!postalCode.trim()) {
    return { isValid: false, error: 'Postal code is required' }
  }

  const normalizedCode = postalCode.trim()
  const pattern = postalCodePatterns[countryCode.toUpperCase()]

  if (pattern) {
    // Country-specific validation
    if (!pattern.test(normalizedCode)) {
      return {
        isValid: false,
        error: `Please enter a valid postal code for ${countryCode}`,
      }
    }
  } else {
    // Generic validation for unsupported countries
    if (!genericPostalCodePattern.test(normalizedCode)) {
      return {
        isValid: false,
        error: 'Please enter a valid postal code',
      }
    }
  }

  return { isValid: true }
}

/**
 * Formats a postal code for display based on country
 * @param postalCode - The postal code to format
 * @param countryCode - The country code
 * @returns Formatted postal code
 */
export const formatPostalCode = (
  postalCode: string,
  countryCode: string,
): string => {
  const normalizedCode = postalCode.trim().toUpperCase()

  switch (countryCode.toUpperCase()) {
    case 'NL':
      // Format as 1234 AB
      return normalizedCode.replace(/(\d{4})([A-Z]{2})/, '$1 $2')
    case 'US':
      // Format as 12345 or 12345-6789
      return normalizedCode
    case 'GB':
      // Format as A1 1AA
      return normalizedCode.replace(
        /([A-Z]{1,2}\d[A-Z\d]?)(\d[A-Z]{2})/,
        '$1 $2',
      )
    case 'CA':
      // Format as A1A 1A1
      return normalizedCode.replace(/([A-Z]\d[A-Z])(\d[A-Z]\d)/, '$1 $2')
    default:
      return normalizedCode
  }
}

/**
 * Checks if a postal code is potentially valid (for real-time validation)
 * @param postalCode - The postal code to check
 * @param countryCode - The country code
 * @returns True if the postal code could be valid
 */
export const isPostalCodePotentiallyValid = (
  postalCode: string,
  countryCode: string,
): boolean => {
  if (!postalCode.trim()) return true // Allow empty for real-time validation

  const normalizedCode = postalCode.trim()

  // Very permissive character validation for search input - just block obviously invalid characters
  const validCharacters = /^[A-Z0-9\s-]*$/i

  if (!validCharacters.test(normalizedCode)) {
    return false
  }

  // Very lenient length checks since this is a search input that gets trimmed on API submission
  switch (countryCode.toUpperCase()) {
    case 'NL':
      // Netherlands: be very permissive, just check reasonable length
      return normalizedCode.length <= 15

    case 'US':
      // US: allow generous length for ZIP+4 with spaces
      return normalizedCode.length <= 15

    case 'GB':
      // UK: very permissive for various formats
      return normalizedCode.length <= 15

    case 'CA':
      // Canada: permissive for postal code format
      return normalizedCode.length <= 15

    case 'DE':
    case 'FR':
    case 'IT':
    case 'SE':
    case 'PL':
    case 'ES':
    case 'FI':
    case 'CZ':
    case 'HR':
    case 'HU':
    case 'EE':
    case 'LT':
    case 'GR':
    case 'BE':
    case 'CH':
    case 'DK':
    case 'AT':
    case 'NO':
    case 'LV':
      // All European countries: very permissive length
      return normalizedCode.length <= 15

    default:
      // Generic validation: very permissive for search
      return normalizedCode.length <= 20
  }
}
